import React, { useState } from 'react';
import GameModal from '../GameModal/GameModal.tsx';
import { useAuth } from '../../supabase/AuthContext.tsx';

import './AuthModal.css';

type AuthTab = 'sign-in' | 'sign-up';

const AuthModal: React.FC = () => {
  const { isAuthModalOpen, closeAuthModal, signIn, signUp, signInWithOAuth } = useAuth();
  const [activeTab, setActiveTab] = useState<AuthTab>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  function resetForm() {
    setEmail('');
    setPassword('');
    setError(null);
    setIsSubmitting(false);
    setSignUpSuccess(false);
  }

  function handleClose() {
    resetForm();
    closeAuthModal();
  }

  function switchTab(tab: AuthTab) {
    setActiveTab(tab);
    setError(null);
    setSignUpSuccess(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (activeTab === 'sign-in') {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          handleClose();
        }
      } else {
        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          setSignUpSuccess(true);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    const { error } = await signInWithOAuth('google');
    if (error) {
      setError(error.message);
    }
  }

  const content = (
    <div className='authModalContent'>
      <div className='authTabs'>
        <button
          className={`authTab ${activeTab === 'sign-in' ? 'authTabActive' : ''}`}
          onClick={() => switchTab('sign-in')}
        >
          Sign In
        </button>
        <button
          className={`authTab ${activeTab === 'sign-up' ? 'authTabActive' : ''}`}
          onClick={() => switchTab('sign-up')}
        >
          Create Account
        </button>
      </div>

      {signUpSuccess ? (
        <div className='authSuccess'>
          Check your email for a confirmation link to complete your account setup.
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} className='authForm'>
            <input
              type='email'
              placeholder='Email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className='authInput'
              required
              autoComplete='email'
            />
            <input
              type='password'
              placeholder='Password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className='authInput'
              required
              autoComplete={activeTab === 'sign-up' ? 'new-password' : 'current-password'}
              minLength={6}
            />
            {error && <div className='authError'>{error}</div>}
            <button
              type='submit'
              className='authSubmitButton'
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Loading...'
                : activeTab === 'sign-in'
                  ? 'Sign In'
                  : 'Create Account'}
            </button>
          </form>

          <div className='authDivider'>
            <span>or</span>
          </div>

          <button
            className='authOAuthButton'
            onClick={handleGoogleSignIn}
          >
            <svg width='18' height='18' viewBox='0 0 48 48'>
              <path fill='#EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/>
              <path fill='#4285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/>
              <path fill='#FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/>
              <path fill='#34A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/>
            </svg>
            Sign in with Google
          </button>
        </>
      )}

      <div className='authFooter'>
        Save your stats across devices and never lose your progress.
      </div>
    </div>
  );

  if (!isAuthModalOpen) return null;

  return (
    <GameModal
      title={activeTab === 'sign-in' ? 'Sign In' : 'Create Account'}
      content={content}
      isOpen={isAuthModalOpen}
      onClose={handleClose}
    />
  );
};

export default AuthModal;
